import React, { FC, memo, useEffect, useMemo } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { IUser } from '@shared/context/current-user';
import RestaurantMarker from '../RestaurantMarker';
import OrderMarker from '../OrderMarker';
import CourierMarker from '../CourierMarker';
import GoogleMapWrapper from '../GoogleMapWrapper';
import { useOrdersState } from '../../context/orders';
import { CourierReturnStatusType, useCouriersActions, useCouriersState } from '../../context/couriers';
import CourierRoute from '../CourierRoute';
import { DeliveryMenuNavigationItem } from '../../context/delivery-navigation';
import CourierDetail from '../CourierDetail';
import OrderDetail from '../OrderDetail';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useAccordionState } from '@src/shared/context/accordion';
import { useOrderFilterState } from '../../context/order-filter';
import { useOrdersActions } from '../../context/orders';
import useOnlineStatus from '@src/shared/hooks/use-online-status';
import useStyles from './styles';
import OfflineMapIndicator from '../OfflineMapIndicator';
const mapOptions = {
  disableDefaultUI: true,
};

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

interface MapProps {
  isMobileViewActive?: boolean;
  selectedNavigationItem?: DeliveryMenuNavigationItem;
}

const Map: FC<MapProps> = ({ isMobileViewActive, selectedNavigationItem }) => {
  const { restaurant } = useKeycloakUserState();
  const { couriersById, selectedCourierId } = useCouriersState();
  const { ordersById, selectedOrderId } = useOrdersState();
  const { hoveredOrderReference } = useAccordionState();
  const { activeOrderFilter } = useOrderFilterState();
  const { setSelectedOrderId } = useOrdersActions();
  const { setSelectedCourierId } = useCouriersActions();
  const { isBrowserOnline } = useOnlineStatus()
  const classes = useStyles();
  useEffect(()=>{
    if(selectedCourierId) {
      setSelectedOrderId(null)
    }
  },[selectedCourierId])

  useEffect(()=>{
    if(selectedOrderId) {
      setSelectedCourierId(null)
    }
  },[selectedOrderId])

  const mapCenter = useMemo(() => {
    return {
      lat: (restaurant as IUser)?.location.lat,
      lng: (restaurant as IUser)?.location.lng,
    };
  }, [restaurant]);

  const courierCoordinates = useMemo(() => {
    return {
      lat:
        couriersById[selectedCourierId]?.location?.latitude ||
        ordersById[selectedOrderId]?.location?.latitude ||
        ordersById[hoveredOrderReference]?.location?.latitude ||
        mapCenter.lat,
      lng:
        couriersById[selectedCourierId]?.location?.longitude ||
        ordersById[selectedOrderId]?.location?.longitude ||
        ordersById[hoveredOrderReference]?.location?.longitude ||
        mapCenter.lng,
    };
  }, [selectedCourierId, selectedOrderId, hoveredOrderReference]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY as string,
  });

  if (isLoaded && isBrowserOnline) {
    return (
      <>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={14}
          onCenterChanged={() => {}}
          center={
            selectedCourierId || selectedOrderId || hoveredOrderReference
              ? courierCoordinates
              : mapCenter
          }
          options={mapOptions}
        >
          <GoogleMapWrapper mapCenter={mapCenter}>
            <RestaurantMarker lat={mapCenter.lat} lng={mapCenter.lng} />
            <>
              {selectedCourierId ? (
                <>
                  <CourierRoute {...couriersById[selectedCourierId]} />
                </>
              ) : (
                <>
                  {!selectedOrderId  && Object.keys(couriersById).filter(courierId => {
                    const courier = couriersById[courierId]
                    return !courier.isGuest || (courier.isGuest && courier.returnBack?.status !== CourierReturnStatusType.Returning)
                  }).map((courierId) => (
                    <CourierMarker
                      key={courierId}
                      {...couriersById[courierId]}
                    />
                  ))}
                  {Object.keys(ordersById).filter(a => {
                    if(selectedOrderId) {
                      return a === selectedOrderId
                    }
                    else {
                      return true
                    }
                  }).filter(a => {
                    return activeOrderFilter.length > 0 ? !!activeOrderFilter.includes(ordersById[a].status) : a;
                  }).map((orderId) => (
                    <OrderMarker key={orderId} {...ordersById[orderId]} />
                  ))}
                </>
              )}
            </>
          </GoogleMapWrapper>
        </GoogleMap>
        {/* For Mobile View */}
        {isMobileViewActive &&
        selectedNavigationItem === DeliveryMenuNavigationItem.Map ? (
          <>
            {selectedCourierId ? (
              <CourierDetail
                isMobile={isMobileViewActive}
                {...couriersById[selectedCourierId]}
              />
            ) : null}
            {selectedOrderId ? (
              <OrderDetail
                isMobile={isMobileViewActive}
                {...ordersById[selectedOrderId]}
              />
            ) : null}
          </>
        ) : null}
      </>
    );
  }

  if (!isBrowserOnline) {
    return <OfflineMapIndicator />
  }

  if (loadError) {
    return <p>ERROR: LOADING GOOGLE MAPS FAILED</p>;
  }

 

  return <p>LOADING...</p>;
};

export default memo(Map);
